import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  Box,
  TablePagination
} from '@mui/material';
import { RestoreFromTrash, Delete, Visibility } from '@mui/icons-material';

const PAGE_SIZE = 15;

const FlowBackupList = ({ open, onClose, flowId, flowName, onRestore }) => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [viewBackupDialog, setViewBackupDialog] = useState(false);
  const [page, setPage] = useState(0);

  const loadBackups = useCallback(async () => {
    setLoading(true);
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/flow-backups/flow/${flowId}`);

      if (response.ok) {
        const data = await response.json();
        setBackups(data);
      } else {
        console.error('Failed to load backups');
      }
    } catch (error) {
      console.error('Error loading backups:', error);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    if (open && flowId) {
      setPage(0);
      loadBackups();
    }
  }, [open, flowId, loadBackups]);

  const pageSlice = useMemo(() => {
    const start = page * PAGE_SIZE;
    return backups.slice(start, start + PAGE_SIZE);
  }, [backups, page]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(backups.length / PAGE_SIZE) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [backups.length, page]);

  const handleRestore = async (backupId) => {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/flow-backups/${backupId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        if (onRestore) {
          onRestore();
        }
        onClose();
      } else {
        console.error('Failed to restore backup');
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
    }
  };

  const handleDelete = async (backupId) => {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/flow-backups/${backupId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadBackups();
      } else {
        console.error('Failed to delete backup');
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
    }
  };

  const handleViewBackup = async (backupId) => {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/flow-backups/${backupId}`);

      if (response.ok) {
        const backup = await response.json();
        setSelectedBackup(backup);
        setViewBackupDialog(true);
      } else {
        console.error('Failed to load backup details');
      }
    } catch (error) {
      console.error('Error loading backup details:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getBackupReasonLabel = (reason) => {
    return reason === 'auto' ? 'Auto' : 'Manual';
  };

  const getBackupReasonColor = (reason) => {
    return reason === 'auto' ? 'default' : 'primary';
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{`Backups for "${flowName}"`}</DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography>Loading backups…</Typography>
          ) : backups.length === 0 ? (
            <Typography>No backups found</Typography>
          ) : (
            <>
              <List dense disablePadding>
                {pageSlice.map((backup) => (
                  <ListItem key={backup._id} divider>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body1">{formatDate(backup.createdAt)}</Typography>
                          <Chip
                            label={getBackupReasonLabel(backup.backupReason)}
                            color={getBackupReasonColor(backup.backupReason)}
                            size="small"
                          />
                          {backup.description && (
                            <Typography variant="body2" color="textSecondary">
                              — {backup.description}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          Nodes: {backup.nodesCount || 0} | Edges: {backup.edgesCount || 0}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="view"
                        onClick={() => handleViewBackup(backup._id)}
                        size="small"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="restore"
                        onClick={() => handleRestore(backup._id)}
                        size="small"
                        color="primary"
                      >
                        <RestoreFromTrash />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(backup._id)}
                        size="small"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <TablePagination
                component="div"
                count={backups.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelRowsPerPage="Per page"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewBackupDialog} onClose={() => setViewBackupDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>View backup — {selectedBackup && formatDate(selectedBackup.createdAt)}</DialogTitle>
        <DialogContent>
          {selectedBackup && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Backup details
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Reason:</strong> {getBackupReasonLabel(selectedBackup.backupReason)}
              </Typography>
              {selectedBackup.description && (
                <Typography variant="body2" paragraph>
                  <strong>Description:</strong> {selectedBackup.description}
                </Typography>
              )}
              <Typography variant="h6" gutterBottom>
                Flow data
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '400px',
                  fontSize: '12px'
                }}
              >
                {JSON.stringify(
                  {
                    nodes: selectedBackup.nodes,
                    edges: selectedBackup.edges,
                    viewport: selectedBackup.viewport
                  },
                  null,
                  2
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewBackupDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FlowBackupList;
